import _ from 'lodash';

import Entity from './Entity';
import LineItemEntry from './LineItemEntry';
import { ParkerMeta } from './Parker';
import { RateMeta } from './Rate';
import { FeeMeta } from './Fee';
import Validation from './Validation';

const dynamicAllowedValues = {
  line_item_rate_type: { enumName: 'LineItemRateTypeEnum' }
};

export default class InvoiceLineItem {
  fields = {
    id: {
      value: null
    },
    balance: {
      value: null
    },
    adjusted_gross_amount: {
      value: null
    },
    adjusted_gross_amounts: {
      value: null
    },
    invoice_id: {
      value: null
    },
    entries: {
      value: []
    },
    parker: {
      value: null
    },
    rate: {
      value: null
    },
    fee: {
      value: null
    },
    validation: {
      value: null
    },
    created_at: {
      value: null
    },
    line_item_rate_type: {
      value: null
    }
  };


  static createFromQueryValues(values) {
    const invoiceLineItem = new this();

    for (let field in invoiceLineItem.fields) {
      if (values.hasOwnProperty(field)) {
        switch (field) {
          case 'entries':
            if (Array.isArray(values[field])) {
              for (let entry of values[field]) {
                invoiceLineItem.fields[field].value.push(LineItemEntry.createFromQueryValues(entry));
              }
              invoiceLineItem.fields[field].value.sort((a, b) => {
                const sortValueA = a.fields.created_at.value;
                const sortValueB = b.fields.created_at.value;
                return (sortValueA <= sortValueB) ? (sortValueA < sortValueB) ? -1 : 0 : 1;
              });
            }
            break;
          case 'parker':
            invoiceLineItem.fields[field].value = (values[field] === null) ?
              values[field] :
              ParkerMeta.createFromQueryValues(values[field]);
            break;
          case 'rate':
            invoiceLineItem.fields[field].value = (values[field] === null) ?
              values[field] :
              RateMeta.createFromQueryValues(values[field]);
            break;
          case 'fee':
            invoiceLineItem.fields[field].value = (values[field] === null) ?
              values[field] :
              FeeMeta.createFromQueryValues(values[field]);
            break;
          case 'validation':
            invoiceLineItem.fields[field].value = (values[field] === null) ?
              values[field] :
              Validation.createFromQueryValues(values[field]);
            break;
          case 'created_at':
            invoiceLineItem.fields[field].value = (values[field] === null) ?
              values[field] :
              new Date(Number(values[field]));
            break;
          default:
            invoiceLineItem.fields[field].value = values[field];
        }
      }
    }

    return invoiceLineItem;
  }


  getQueryFieldValues() {
    const values = _.cloneDeep(LineItemInput);

    if (this.fields.id.value === null) {
      delete values.id;
    }
    else {
      values.id = this.fields.id.value;
    }
    if (Array.isArray(this.fields.entries.value)) {
      const entries = [];
      for (let entry of this.fields.entries.value) {
        // Only sending new entries, so disregard entries that already have ID's
        if (entry.fields.id.value !== null) continue;
        const entryQueryFieldValues = entry.getQueryFieldValues();
        if (entryQueryFieldValues.line_item_id === null) {
          entryQueryFieldValues.line_item_id = this.fields.id.value;
        }
        entries.push(entryQueryFieldValues);
      }
      if (entries.length) {
        values.entries = entries
      }
      else {
        delete values.entries;
      }
    }
    if (this.fields.parker.value !== null) {
      values.parker_meta = this.fields.parker.value.getLineItemParkerMetaValues();
    }
    else {
      delete values.parker_meta;
    }

    // for some reason if doesn't have an id it submits but doesn't include fee meta and including one doesn't submit and errors out.
    if (this.fields.fee.value !== null &&
      this.fields.fee.value.fields.id.value !== null
    ) {
      values.fee_meta = {
        type: this.fields.fee.value.fields.type.value,
        price: this.fields.fee.value.fields.price.value,
        tax_1: this.fields.fee.value.fields.tax_1.value,
        tax_2: this.fields.fee.value.fields.tax_2.value,
        tax_3: this.fields.fee.value.fields.tax_3.value,
        tax_4: this.fields.fee.value.fields.tax_4.value,
        name: this.fields.fee.value.fields.name.value,
      }
    }
    else {
      delete values.fee_meta;
    }

    if (this.fields.rate.value !== null &&
      this.fields.rate.value.fields.id.value !== null
    ) {
      values.rate_id = this.fields.rate.value.fields.id.value;
    }
    else {
      delete values.rate_id;
    }

    if (this.fields.line_item_rate_type.value !== null) {
      values.line_item_rate_type = this.fields.line_item_rate_type.value;
    }
    else {
      delete values.line_item_rate_type;
    }

    if (this.fields.validation.value !== null) {
      values.validation = this.fields.validation.value.getQueryFieldValues();
    } else {
      delete values.validation;
    }

    return (Object.keys(values).length) ? values : false;
  }


  getAdjustedGrossAmount() {
    let amount = 0;

    for (let entry of this.fields.entries.value) {
      const entryType = entry.fields.type.value;
      switch (entryType) {
        case 'CHARGE':
          amount += Number(entry.getGrossAmount());
          break;
        case 'POSITIVE_ADJUSTMENT':
          amount += entry.getGrossAmount();
          break;
        case 'ADJUSTMENT':
          amount -= entry.getGrossAmount();
          break;
        default:
      }
    }

    return amount;
  }


  getCurrentBalance() {
    let balance = 0;

    for (let entry of this.fields.entries.value) {
      const entryType = entry.fields.type.value;
      switch (entryType) {
        case 'CHARGE':
          balance += Number(entry.getGrossAmount());
          break;
        case 'POSITIVE_ADJUSTMENT':
        case 'REFUND':
        case 'REVERSAL':
        case 'UNAPPLY_CREDIT':
          balance += entry.getGrossAmount();
          break;
        case 'PAYMENT':
        case 'ADJUSTMENT':
        case 'LANDLORD_COLLECTED':
        case 'WRITE_OFF':
          balance -= entry.getGrossAmount();
          break;
        default:
      }
    }

    return balance;
  }

  get reversablePaymentIds() {
    return this.fields.entries.value.reduce((paymentIds, entry) => {
      const lineItemPayment = entry.fields.line_item_payment.value;
      if (lineItemPayment && !lineItemPayment.fields.reversal_entry_id.value) {
        paymentIds.push(lineItemPayment.paymentId);
      }

      return paymentIds;
    }, []);
  }

  updateEntryTransaction(transaction) {
    const lineItemEntry = this.fields.entries.value.find(entry => entry.fields.id.value === transaction.line_item_entry_id);
    lineItemEntry.updateTransaction(transaction);
  }

  getTransactionQueryFields() {
    return this.fields.entries.value.reduce((transactions, entry) => [
      ...transactions,
      ...entry.getTransactionQueryFields()
    ], []);
  }

  static getFieldAllowedValues(fields) {
    return Entity.getFieldAllowedValues(
      {},
      dynamicAllowedValues,
      fields
    );
  }
}


export const LineItemInput = {
  id: null,
  entries: null,
  parker_meta: null,
  fee_meta: null,
  rate_id: null,
  validation: null,
  line_item_rate_type: null
}
